// src/components/reports/ReportDisplay.jsx
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FileText,
  Download,
  ArrowLeft,
  Calendar,
  User,
  BookOpen,
  Users,
  BarChart3,
  Share2,
  Printer,
  Save,
  Eye,
  EyeOff,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  School,
  CheckCircle2,
  Target,
  Award,
  Brain
} from 'lucide-react';

const ReportDisplay = ({ report, onBack, onGenerateNew }) => {
  const [showRawData, setShowRawData] = useState(false);
  const [saving, setSaving] = useState(false);

  const getReportIcon = () => {
    switch (report.type) {
      case 'grade': return Users;
      case 'subject': return BookOpen;
      case 'learner-subject': return User;
      case 'learner-overall': return BarChart3;
      default: return FileText;
    }
  };

  const getReportTitle = () => {
    switch (report.type) {
      case 'grade':
        return `Grade ${report.grade} Performance Report`;
      case 'subject':
        return `Grade ${report.grade} ${report.subject} Subject Report`;
      case 'learner-subject':
        return `${report.learnerSurname}, ${report.learnerName} - ${report.subject} Report`;
      case 'learner-overall':
        return `${report.learnerSurname}, ${report.learnerName} - Overall Academic Report`;
      default:
        return 'Performance Report';
    }
  };

  const handleDownloadReport = () => {
    const reportContent = formatReportForDownload();
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${getReportTitle().replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrintReport = () => {
  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  
  // Get the rendered markdown content from the existing component
  const printContent = `
<!DOCTYPE html>
<html>
<head>
    <title>${getReportTitle()}</title>
    <style>
        @media print {
            @page { margin: 0.75in; size: A4; }
            body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                margin: 0; padding: 0; line-height: 1.4; color: #000; font-size: 11pt;
            }
            .header { 
                border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 25px;
            }
            .title { font-size: 18pt; font-weight: bold; margin-bottom: 8px; }
            .subtitle { color: #333; margin-bottom: 4px; font-size: 10pt; }
            
            /* Let browser handle table styling naturally */
            table { 
                border-collapse: collapse; width: 100%; margin: 15pt 0; font-size: 10pt;
            }
            th, td { 
                border: 1pt solid #000; padding: 6pt 8pt; text-align: left;
            }
            th { 
                background-color: #f0f0f0 !important; font-weight: bold;
            }
            
            h1 { font-size: 14pt; font-weight: bold; margin: 20pt 0 10pt 0; }
            h2 { font-size: 12pt; font-weight: bold; margin: 15pt 0 8pt 0; }
            h3 { font-size: 11pt; font-weight: bold; margin: 12pt 0 6pt 0; }
            p { margin: 0 0 8pt 0; }
            ul, ol { margin: 8pt 0; padding-left: 20pt; }
            li { margin: 2pt 0; }
        }
        
        @media screen {
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 40px; line-height: 1.6; }
            .header { border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
            .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
            .subtitle { color: #666; margin-bottom: 5px; }
            table { border-collapse: collapse; width: 100%; margin: 1em 0; }
            th, td { border: 1px solid #666; padding: 8px 12px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="title">${getReportTitle()}</div>
        <div class="subtitle">Generated on: ${report.generatedAt.toLocaleDateString()}</div>
        <div class="subtitle">Report Type: ${report.type}</div>
    </div>
    <div id="content"></div>
    
    <script>
        // Let ReactMarkdown render the content, then copy it
        window.addEventListener('load', function() {
            setTimeout(function() {
                window.print();
            }, 500);
        });
    </script>
</body>
</html>`;
  
  printWindow.document.write(printContent);
  printWindow.document.close();
  
  // Copy the rendered content from our existing markdown component
  setTimeout(() => {
    const markdownContent = document.querySelector('.prose').innerHTML;
    printWindow.document.getElementById('content').innerHTML = markdownContent;
    printWindow.print();
  }, 100);
};

  const formatReportForDownload = () => {
    return `${getReportTitle()}
Generated on: ${report.generatedAt.toLocaleDateString()}

${report.aiAnalysis}

---
Report Statistics:
${JSON.stringify(report.stats, null, 2)}

---
Raw Data:
${JSON.stringify(report.rawData, null, 2)}`;
  };

  // Updated print formatting with clean, professional table styling
const formatReportForPrint = () => {
  return `
<!DOCTYPE html>
<html>
<head>
    <title>${getReportTitle()}</title>
    <style>
        /* Base Print Styles */
        @media print {
            @page {
                margin: 0.75in;
                size: A4;
            }
            
            body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                margin: 0;
                padding: 0;
                line-height: 1.4; 
                color: #000;
                font-size: 11pt;
                background: white;
            }
            
            /* Header Styling */
            .header { 
                border-bottom: 2px solid #000; 
                padding-bottom: 15px; 
                margin-bottom: 25px;
                page-break-after: avoid;
            }
            
            .title { 
                font-size: 18pt; 
                font-weight: bold; 
                margin-bottom: 8px; 
            }
            
            .subtitle { 
                color: #333; 
                margin-bottom: 4px;
                font-size: 10pt;
            }
            
            /* Content Styling */
            .markdown-content {
                orphans: 3;
                widows: 3;
            }
            
            .markdown-content h1 {
                font-size: 14pt;
                font-weight: bold;
                margin: 20pt 0 10pt 0;
                color: #000;
                border-bottom: 1px solid #000;
                padding-bottom: 4pt;
                page-break-after: avoid;
            }
            
            .markdown-content h2 {
                font-size: 12pt;
                font-weight: bold;
                margin: 15pt 0 8pt 0;
                color: #000;
                page-break-after: avoid;
            }
            
            .markdown-content h3 {
                font-size: 11pt;
                font-weight: bold;
                margin: 12pt 0 6pt 0;
                color: #000;
                page-break-after: avoid;
            }
            
            .markdown-content p {
                margin: 0 0 8pt 0;
                text-align: justify;
            }
            
            .markdown-content ul, .markdown-content ol {
                margin: 8pt 0;
                padding-left: 20pt;
            }
            
            .markdown-content li {
                margin: 2pt 0;
            }
            
            /* Clean, Professional Table Print Styles */
            .markdown-content table {
                border-collapse: collapse;
                width: 100%;
                margin: 15pt 0;
                font-size: 10pt;
                page-break-inside: auto;
            }
            
            .markdown-content th, .markdown-content td {
                border: 1pt solid #000;
                padding: 6pt 8pt;
                text-align: left;
                vertical-align: top;
            }
            
            .markdown-content th {
                background-color: #f0f0f0 !important;
                font-weight: bold;
                color: #000 !important;
                page-break-after: avoid;
            }
            
            .markdown-content tbody tr {
                page-break-inside: avoid;
            }
            
            /* Table headers repeat on new pages */
            .markdown-content thead {
                display: table-header-group;
            }
            
            .markdown-content tbody {
                display: table-row-group;
            }
            
            /* Other content styling */
            .markdown-content strong {
                font-weight: bold;
                color: #000;
            }
            
            .markdown-content em {
                font-style: italic;
            }
            
            .markdown-content code {
                background-color: #f5f5f5;
                padding: 1pt 2pt;
                border: 0.5pt solid #ccc;
                font-family: 'Courier New', monospace;
                font-size: 9pt;
            }
            
            .markdown-content blockquote {
                border-left: 2pt solid #000;
                padding-left: 8pt;
                margin: 8pt 0;
                font-style: italic;
                page-break-inside: avoid;
            }
            
            /* Hide non-essential elements */
            .no-print {
                display: none !important;
            }
        }
        
        /* Screen styles for preview */
        @media screen {
            body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                margin: 40px; 
                line-height: 1.6; 
                color: #333;
                background: white;
            }
            
            .header { 
                border-bottom: 2px solid #333; 
                padding-bottom: 20px; 
                margin-bottom: 30px; 
            }
            
            .title { 
                font-size: 24px; 
                font-weight: bold; 
                margin-bottom: 10px; 
            }
            
            .subtitle { 
                color: #666; 
                margin-bottom: 5px; 
            }
            
            /* Clean table styling for screen preview */
            .markdown-content table {
                border-collapse: collapse;
                width: 100%;
                margin: 1em 0;
            }
            
            .markdown-content th, .markdown-content td {
                border: 1px solid #666;
                padding: 8px 12px;
                text-align: left;
            }
            
            .markdown-content th {
                background-color: #f0f0f0;
                font-weight: bold;
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="title">${getReportTitle()}</div>
        <div class="subtitle">Generated on: ${report.generatedAt.toLocaleDateString()}</div>
        <div class="subtitle">Report Type: ${report.type}</div>
        ${report.context ? `<div class="subtitle">Context: ${report.context}</div>` : ''}
    </div>
    <div class="markdown-content">${convertMarkdownToHTMLForPrint(report.aiAnalysis)}</div>
</body>
</html>`;
};

// Simplified markdown to HTML converter for print
const convertMarkdownToHTMLForPrint = (markdown) => {
  let html = markdown;
  
  // Convert headers
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  
  // Convert text formatting
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Handle tables - simplified approach
  html = html.replace(/\|(.+)\|/g, (match, content) => {
    const cells = content.split('|').map(cell => cell.trim());
    return '<tr>' + cells.map(cell => {
      // Skip separator rows
      if (cell.match(/^-+$/)) {
        return '';
      }
      return `<td>${cell || ''}</td>`;
    }).join('') + '</tr>';
  });
  
  // Wrap table rows in simple table structure
  html = html.replace(/(<tr>.*?<\/tr>)/gs, (match) => {
    const rows = match.match(/<tr>.*?<\/tr>/g);
    if (!rows) return match;
    
    // Simple assumption: first row is header
    const headerRow = rows[0].replace(/<td>/g, '<th>').replace(/<\/td>/g, '</th>');
    const bodyRows = rows.slice(1);
    
    return `
      <table>
        <thead>${headerRow}</thead>
        <tbody>${bodyRows.join('')}</tbody>
      </table>
    `;
  });
  
  // Handle lists
  html = html.replace(/^\- (.*$)/gim, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
  
  // Convert line breaks to paragraphs
  html = html.replace(/\n\n/g, '</p><p>');
  html = '<p>' + html + '</p>';
  
  // Clean up
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p>(<table.*?<\/table>)<\/p>/gs, '$1');
  html = html.replace(/<p>(<h[1-6]>.*?<\/h[1-6]>)<\/p>/g, '$1');
  html = html.replace(/<p>(<ul>.*?<\/ul>)<\/p>/gs, '$1');
  
  return html;
};

  // Custom components for markdown rendering
  const markdownComponents = {
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold mt-8 mb-4 text-gray-900 border-b-2 border-gray-200 pb-2">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-bold mt-6 mb-3 text-gray-800">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-700">
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p className="text-gray-700 mb-3 leading-relaxed">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc pl-6 mb-4 space-y-1">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 mb-4 space-y-1">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-gray-700">
        {children}
      </li>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full border border-gray-300 rounded-lg">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gray-50">
        {children}
      </thead>
    ),
    th: ({ children }) => (
      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 border-b border-gray-300">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
        {children}
      </td>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-50 italic">
        {children}
      </blockquote>
    ),
    code: ({ children, inline }) => {
      if (inline) {
        return (
          <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">
            {children}
          </code>
        );
      }
      return (
        <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg overflow-x-auto my-4">
          <code className="font-mono text-sm">{children}</code>
        </pre>
      );
    },
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em className="italic text-gray-800">
        {children}
      </em>
    )
  };

  const ReportIcon = getReportIcon();

  const renderSchoolPerformanceSection = () => {
    if (!report.stats?.schoolAnalysis) return null;
    
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <School className="w-5 h-5 text-blue-600" />
          <h3 className="text-xl font-semibold">School Performance Analysis</h3>
        </div>
        
        {/* Top Performing Schools */}
        {report.stats.topPerformingSchools?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-medium mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-yellow-600" />
              Top Performing Schools
            </h4>
            <div className="grid gap-3">
              {report.stats.topPerformingSchools.map((school, index) => (
                <div key={school.schoolName} className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-green-900">#{index + 1} {school.schoolName}</h5>
                      <p className="text-sm text-green-700">
                        {school.learnerCount} learners • {school.averagePerformance.toFixed(1)}% average
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-800">
                        {school.averagePerformance.toFixed(1)}%
                      </div>
                      <div className="text-xs text-green-600">
                        {school.passRate.toFixed(1)}% pass rate
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Schools Needing Attention */}
        {report.stats.schoolsNeedingAttention?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              Schools Requiring Attention
            </h4>
            <div className="grid gap-3">
              {report.stats.schoolsNeedingAttention.map((school, index) => (
                <div key={school.schoolName} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-red-900">{school.schoolName}</h5>
                      <p className="text-sm text-red-700">
                        {school.learnerCount} learners • Needs support
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-red-800">
                        {school.averagePerformance.toFixed(1)}%
                      </div>
                      <div className="text-xs text-red-600">
                        {school.passRate.toFixed(1)}% pass rate
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Generator
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onGenerateNew}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              New Report
            </button>
            
            <button
              onClick={handlePrintReport}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            
            <button
              onClick={handleDownloadReport}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <ReportIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getReportTitle()}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Generated: {report.generatedAt.toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <Brain className="w-4 h-4" />
                <span>AI-Powered Analysis</span>
              </div>
              <div className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                <span>Report Type: {report.type}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {report.type === 'grade' && (
          <>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-800">Total Learners</h3>
              </div>
              <p className="text-2xl font-bold text-blue-900">{report.stats.totalLearners}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h3 className="font-medium text-green-800">Class Average</h3>
              </div>
              <p className="text-2xl font-bold text-green-900">
                {report.stats.fiveNumberSummary.mean.toFixed(1)}%
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
                <h3 className="font-medium text-yellow-800">Pass Rate</h3>
              </div>
              <p className="text-2xl font-bold text-yellow-900">
                {report.stats.passRate.toFixed(1)}%
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                <h3 className="font-medium text-purple-800">Total Tasks</h3>
              </div>
              <p className="text-2xl font-bold text-purple-900">{report.stats.totalTasks}</p>
            </div>
          </>
        )}

        {report.type === 'subject' && (
          <>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-800">Learners</h3>
              </div>
              <p className="text-2xl font-bold text-blue-900">{report.stats.totalLearners}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h3 className="font-medium text-green-800">Subject Average</h3>
              </div>
              <p className="text-2xl font-bold text-green-900">
                {report.stats.fiveNumberSummary.mean.toFixed(1)}%
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-yellow-600" />
                <h3 className="font-medium text-yellow-800">Pass Rate</h3>
              </div>
              <p className="text-2xl font-bold text-yellow-900">
                {report.stats.passRate.toFixed(1)}%
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <h3 className="font-medium text-purple-800">Assessments</h3>
              </div>
              <p className="text-2xl font-bold text-purple-900">{report.stats.totalAssessments}</p>
            </div>
          </>
        )}

        {(report.type === 'learner-subject' || report.type === 'learner-overall') && (
          <>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-800">Average Performance</h3>
              </div>
              <p className="text-2xl font-bold text-blue-900">
                {report.stats.averagePerformance.toFixed(1)}%
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-green-600" />
                <h3 className="font-medium text-green-800">Highest Mark</h3>
              </div>
              <p className="text-2xl font-bold text-green-900">
                {report.stats.highestMark.toFixed(1)}%
              </p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-orange-600" />
                <h3 className="font-medium text-orange-800">Progression</h3>
              </div>
              <p className="text-2xl font-bold text-orange-900">
                {report.stats.progression > 0 ? '+' : ''}{report.stats.progression.toFixed(1)}%
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600" />
                <h3 className="font-medium text-purple-800">Pass Rate</h3>
              </div>
              <p className="text-2xl font-bold text-purple-900">
                {report.stats.passRate.toFixed(1)}%
              </p>
            </div>
          </>
        )}
      </div>

      {/* AI Analysis with Markdown Rendering */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Brain className="w-5 h-5 text-blue-600" />
              AI-Generated Analysis & Recommendations
            </h2>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <AlertCircle className="w-4 h-4" />
              <span>Powered by Gemini 2.0 Flash</span>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="prose prose-blue max-w-none">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {report.aiAnalysis}
            </ReactMarkdown>
          </div>
        </div>
      </div>

      {/* Raw Data Toggle */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <button
            onClick={() => setShowRawData(!showRawData)}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900">Technical Data & Statistics</h3>
            <div className="flex items-center gap-2">
              {showRawData ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  <span className="text-sm text-gray-500">Hide Data</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  <span className="text-sm text-gray-500">Show Data</span>
                </>
              )}
            </div>
          </button>
        </div>
        
        {showRawData && (
          <div className="p-6 bg-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Report Statistics</h4>
                <pre className="bg-white p-4 rounded border text-sm overflow-auto max-h-64">
                  {JSON.stringify(report.stats, null, 2)}
                </pre>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Raw Data Summary</h4>
                <pre className="bg-white p-4 rounded border text-sm overflow-auto max-h-64">
                  {JSON.stringify(report.rawData, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDisplay;